import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { RouterModule } from '@angular/router';
import { FormsModule } from '@angular/forms';
import { ExternalDataService, VoucherDto, VoucherFilterDto, VoucherStatus } from '../../services/externalDataService.service';
import { AuthService } from '../../services/auth.service';
import { Observable, filter, take } from 'rxjs';
import { PaginationComponent } from '../shared/pagination/pagination.component';

@Component({
  selector: 'app-vouchers',
  standalone: true,
  imports: [CommonModule, RouterModule, FormsModule, PaginationComponent],
  templateUrl: './vouchers.component.html',
  styleUrl: './vouchers.component.css'
})
export class VouchersComponent implements OnInit {
  vouchers: VoucherDto[] = [];
  expandedRows: Set<string> = new Set();
  loading = false;
  errorMessage = '';
  
  // Pagination properties
  currentPage = 1;
  pageSize = 10;
  totalCount: number = 0;
  totalPages: number = 1;
  
  // Filter properties
  filters: VoucherFilterDto = {
    phoneNumber: null,
    customerName: null,
    partnerName: null,
    minVoucherAmount: null,
    maxVoucherAmount: null,
    partnerId: null,
    outletId: null,
    status: null,
    redemptionReference: null,
    fromDate: null,
    toDate: null,
    pageIndex: 0,
    pageSize: 10
  };
  
  // UI status filter (VoucherStatus enum for dropdown)
  statusFilter: VoucherStatus | null = null;
  
  // Track if filters have been applied to enable Apply button when clearing filters
  filtersApplied: boolean = false;
  
  // Dropdown data
  outlets: { id: string; name: string }[] = [];
  
  // Make Math available in template
  Math = Math;
  VoucherStatus = VoucherStatus;

  constructor(
    private externalDataService: ExternalDataService,
    private authService: AuthService
  ) {}

  ngOnInit(): void {
    // Wait for MSAL to be initialized before loading vouchers
    this.authService.getMsalInitializedObservable()
      .pipe(
        filter(initialized => initialized),
        take(1)
      )
      .subscribe(() => {
        this.loadVouchers();
      });
  }

  extractUniqueOutlets(): void {
    // Extract unique outlets from loaded vouchers
    const outletMap = new Map<string, string>();
    this.vouchers.forEach(voucher => {
      if (voucher.outletId && voucher.outletName) {
        if (!outletMap.has(voucher.outletId)) {
          outletMap.set(voucher.outletId, voucher.outletName);
        }
      }
    });
    this.outlets = Array.from(outletMap.entries()).map(([id, name]) => ({ id, name }));
  }

  loadVouchers(): void {
    this.loading = true;
    this.errorMessage = '';
    
    // Convert page number to page index (page index is 0-based)
    const pageIndex = this.currentPage - 1;
    
    // Prepare filter object
    const statusValue = this.statusFilter === null || this.statusFilter === undefined 
      ? null 
      : this.getStatusNumericValue(this.statusFilter);
    
    const filter: VoucherFilterDto = {
      pageIndex: pageIndex,
      pageSize: this.pageSize,
      phoneNumber: this.filters.phoneNumber?.trim() || null,
      customerName: this.filters.customerName?.trim() || null,
      partnerName: this.filters.partnerName?.trim() || null,
      minVoucherAmount: this.filters.minVoucherAmount ?? null,
      maxVoucherAmount: this.filters.maxVoucherAmount ?? null,
      partnerId: this.filters.partnerId?.trim() || null,
      outletId: this.filters.outletId?.trim() || null,
      status: statusValue,
      redemptionReference: this.filters.redemptionReference?.trim() || null,
      fromDate: this.filters.fromDate || null,
      toDate: this.filters.toDate || null
    };
    
    this.externalDataService.getFilteredVouchers(filter).subscribe({
      next: (response) => {
        this.vouchers = response.items || [];
        this.totalCount = response.totalCount || 0;
        this.calculateTotalPages();
        this.extractUniqueOutlets();
        this.loading = false;
      },
      error: (error) => {
        console.error('Error loading vouchers:', error);
        console.error('Error details:', error.error);
        this.errorMessage = 'Failed to load vouchers. Please try again.';
        this.loading = false;
        this.vouchers = [];
        this.totalCount = 0;
      }
    });
  }

  calculateTotalPages(): void {
    // Calculate total pages based on totalCount from API
    this.totalPages = Math.ceil(this.totalCount / this.pageSize) || 1;
  }

  onPageChange(page: number): void {
    this.currentPage = page;
    this.expandedRows.clear(); // Clear expanded rows when changing pages
    this.loadVouchers();
  }

  onPageSizeChange(size: number): void {
    this.pageSize = size;
    this.currentPage = 1; // Reset to first page when changing page size
    this.expandedRows.clear(); // Clear expanded rows when changing page size
    this.loadVouchers();
  }

  toggleRow(voucherId: string): void {
    if (this.expandedRows.has(voucherId)) {
      this.expandedRows.delete(voucherId);
    } else {
      this.expandedRows.add(voucherId);
    }
  }

  isRowExpanded(voucherId: string): boolean {
    return this.expandedRows.has(voucherId);
  }

  // Filter methods
  onFilterChange(): void {
    // Optional: Implement debounced search or real-time filtering
  }

  applyFilters(): void {
    this.currentPage = 1; // Reset to first page when applying filters
    this.expandedRows.clear(); // Clear expanded rows when filtering
    this.filtersApplied = true; // Mark that filters have been applied
    this.loadVouchers();
  }

  clearFilters(): void {
    this.filters = {
      phoneNumber: null,
      customerName: null,
      partnerName: null,
      minVoucherAmount: null,
      maxVoucherAmount: null,
      partnerId: null,
      outletId: null,
      status: null,
      redemptionReference: null,
      fromDate: null,
      toDate: null,
      pageIndex: 0,
      pageSize: 10
    };
    this.statusFilter = null;
    this.filtersApplied = false; // Reset the flag when clearing filters
    this.currentPage = 1; // Reset to first page when clearing filters
    this.expandedRows.clear(); // Clear expanded rows when clearing filters
    this.loadVouchers();
  }

  hasActiveFilters(): boolean {
    // Enable Apply button if there are active filters OR if filters have been applied before
    // (this allows users to clear filters by selecting "All Status" after having a status filter)
    const hasFilters = !!(
      this.filters.phoneNumber?.trim() || 
      this.filters.customerName?.trim() || 
      this.filters.partnerName?.trim() || 
      this.filters.minVoucherAmount !== null ||
      this.filters.maxVoucherAmount !== null ||
      this.filters.partnerId?.trim() ||
      this.filters.outletId?.trim() ||
      this.statusFilter !== null ||
      this.filters.redemptionReference?.trim() ||
      this.filters.fromDate ||
      this.filters.toDate
    );
    
    // If filters were previously applied, enable the button even if current state has no active filters
    // This allows users to apply "All Status" to clear a previously applied status filter
    return hasFilters || this.filtersApplied;
  }

  formatDate(dateString: string | null | undefined): string {
    if (!dateString) return 'N/A';
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('en-US', { 
        year: 'numeric', 
        month: 'short', 
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch {
      return 'Invalid Date';
    }
  }

  formatCurrency(amount: number): string {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      minimumFractionDigits: 2
    }).format(amount);
  }

  getStatusNumericValue(status: VoucherStatus | null): number | null {
    if (status === null || status === undefined) return null;
    switch (status) {
      case VoucherStatus.Active:
        return 0;
      case VoucherStatus.Redeemed:
        return 1;
      case VoucherStatus.Expired:
        return 2;
      case VoucherStatus.Cancelled:
        return 3;
      default:
        return null;
    }
  }

  getStatusText(status: VoucherStatus | string | number): string {
    // Handle numeric enum values from API (0, 1, 2, 3)
    if (typeof status === 'number') {
      switch (status) {
        case 0:
          return 'Active';
        case 1:
          return 'Redeemed';
        case 2:
          return 'Expired';
        case 3:
          return 'Cancelled';
        default:
          return 'Unknown';
      }
    }
    // Handle string status values from API
    if (typeof status === 'string') {
      return status;
    }
    // Handle enum values
    switch (status) {
      case VoucherStatus.Active:
        return 'Active';
      case VoucherStatus.Redeemed:
        return 'Redeemed';
      case VoucherStatus.Expired:
        return 'Expired';
      case VoucherStatus.Cancelled:
        return 'Cancelled';
      default:
        return String(status);
    }
  }

  getStatusClass(status: VoucherStatus | string | number): string {
    // Handle numeric enum values from API (0, 1, 2, 3)
    if (typeof status === 'number') {
      switch (status) {
        case 0:
          return 'status-active';
        case 1:
          return 'status-redeemed';
        case 2:
          return 'status-expired';
        case 3:
          return 'status-cancelled';
        default:
          return 'status-default';
      }
    }
    // Handle string status values
    if (typeof status === 'string') {
      switch (status) {
        case 'Active':
          return 'status-active';
        case 'Redeemed':
          return 'status-redeemed';
        case 'Expired':
          return 'status-expired';
        case 'Cancelled':
          return 'status-cancelled';
        default:
          return 'status-default';
      }
    }
    // Handle enum values
    switch (status) {
      case VoucherStatus.Active:
        return 'status-active';
      case VoucherStatus.Redeemed:
        return 'status-redeemed';
      case VoucherStatus.Expired:
        return 'status-expired';
      case VoucherStatus.Cancelled:
        return 'status-cancelled';
      default:
        return 'status-default';
    }
  }

}
